// src/components/ReportesModal.js
import React, { useState } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import ExcelJS from 'exceljs';
import './ReportesModal.css';
import { FaTimes, FaDownload, FaSpinner, FaCalendarAlt, FaCalendarCheck, FaBolt, FaHistory, FaFileExcel, FaFileInvoiceDollar, FaChartBar, FaFileAlt } from 'react-icons/fa';
import { calcularEstadoMoroso, useConfiguracionFinanciera } from '../utils/morosoUtils';

const ReportesModal = ({ isOpen, onClose, fraccionamientoId, reportType = 'mensual' }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Helper: Aplicar atajos rápidos de período
    const aplicarAtajoPeriodo = (tipo) => {
        const ahora = new Date();
        if (tipo === 'actual') {
            setSelectedMonth(ahora.getMonth() + 1);
            setSelectedYear(ahora.getFullYear());
        } else if (tipo === 'anterior') {
            const mesAnt = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
            setSelectedMonth(mesAnt.getMonth() + 1);
            setSelectedYear(mesAnt.getFullYear());
        } else if (tipo === 'haceDos') {
            const mesDos = new Date(ahora.getFullYear(), ahora.getMonth() - 2, 1);
            setSelectedMonth(mesDos.getMonth() + 1);
            setSelectedYear(mesDos.getFullYear());
        }
    };

    // Helper: Comprobar si el atajo coincide con el período seleccionado
    const esAtajoActivo = (tipo) => {
        const ahora = new Date();
        if (tipo === 'actual') {
            return selectedMonth === (ahora.getMonth() + 1) && selectedYear === ahora.getFullYear();
        }
        if (tipo === 'anterior') {
            const mesAnt = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
            return selectedMonth === (mesAnt.getMonth() + 1) && selectedYear === mesAnt.getFullYear();
        }
        if (tipo === 'haceDos') {
            const mesDos = new Date(ahora.getFullYear(), ahora.getMonth() - 2, 1);
            return selectedMonth === (mesDos.getMonth() + 1) && selectedYear === mesDos.getFullYear();
        }
        return false;
    };

    // Estados para paginación y pestañas del reporte mensual
    const [activeTabIngEgr, setActiveTabIngEgr] = useState('ingresos');
    const [currentPageIngresos, setCurrentPageIngresos] = useState(1);
    const [currentPageEgresos, setCurrentPageEgresos] = useState(1);
    const ITEMS_PER_PAGE = 12;

    const handleNextPageIngresos = () => setCurrentPageIngresos(prev => prev + 1);
    const handlePrevPageIngresos = () => setCurrentPageIngresos(prev => Math.max(prev - 1, 1));
    const handleNextPageEgresos = () => setCurrentPageEgresos(prev => prev + 1);
    const handlePrevPageEgresos = () => setCurrentPageEgresos(prev => Math.max(prev - 1, 1));


    // Obtener configuración financiera para cálculo de morosos
    const financialConfig = useConfiguracionFinanciera(fraccionamientoId);

    const generateReporteMensual = async () => {
        try {
            setLoading(true);
            setError(null);

            // Reusar la función existente generarReporteDetallado (callable v2)
            const generarReporteDetallado = httpsCallable(functions, 'generarReporteDetallado');
            const result = await generarReporteDetallado({
                fraccionamientoId,
                mes: selectedMonth,
                anio: selectedYear,
                año: selectedYear,
            });

            // Normalizar para la UI esperada (totales + ingresos/egresos)
            const data = result.data || {};

            // Aplicar el filtro quirúrgico para excluir conceptos de adeudo directamente de la fuente UI
            const ingresosRaw = data.ingresos || [];
            const ingresos = ingresosRaw.filter(ing => {
                const concepto = String(ing.concepto || ing.descripcion || '').toLowerCase();
                const metodoPago = String(ing.metodoPago || '').trim();

                // Bloqueo estricto de banderas visuales
                if (concepto.includes('saldo final de mes')) return false;

                // Si es un cargo automático del sistema, lo excluimos del reporte de flujo
                if (metodoPago === 'registro_automatico') return false;

                // Si no hay método de pago, filtramos marcadores de adeudo
                const esIndicadorVisual = !metodoPago || metodoPago === 'N/A' || metodoPago === '';
                if (esIndicadorVisual) {
                    if (concepto.includes('adeudo historico') ||
                        concepto.includes('adeudo histórico') ||
                        concepto.includes('adeudo cuota de mantenimiento') ||
                        concepto.includes('sin adeudo cuota de mantenimiento') ||
                        concepto.includes('cuota extraordinaria') ||
                        concepto.includes('recargo por morosidad')) {
                        return false;
                    }
                }
                return true;
            });

            const egresos = data.egresos || [];
            const totalIngresos = ingresos.reduce((a, i) => a + (Number(i.montoNeto ?? i.monto) || 0), 0);
            const totalEgresos = egresos.reduce((a, e) => a + (Number(e.monto) || 0), 0);
            const balance = totalIngresos - totalEgresos;

            setReportData({
                totalIngresos,
                totalEgresos,
                balance,
                ingresos,
                egresos,
                notas: data.notas || '',
            });
        } catch (err) {
            // Mostrar mensaje de backend si viene en HttpsError
            const msg = err?.message || err?.toString() || 'Error al generar el reporte';
            setError(msg);
            console.error('Error generarReporteMensual(detallado):', err);
        } finally {
            setLoading(false);
        }
    };

    const generateEstadoCuenta = async () => {
        try {
            setLoading(true);
            setError(null);
            const generarEstadoCuentaGeneral = httpsCallable(functions, 'generarEstadoCuentaGeneral');
            const result = await generarEstadoCuentaGeneral({
                fraccionamientoId,
                mes: selectedMonth,
                anio: selectedYear,
                año: selectedYear,
            });
            setReportData(result.data);
        } catch (err) {
            setError(err.message || 'Error al generar el reporte');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const generateAnalisisGastos = async () => {
        try {
            setLoading(true);
            setError(null);
            const generarAnalisisGastos = httpsCallable(functions, 'generarAnalisisGastos');
            const result = await generarAnalisisGastos({
                fraccionamientoId,
                mes: selectedMonth,
                anio: selectedYear,
                año: selectedYear,
            });
            setReportData(result.data);
        } catch (err) {
            setError(err.message || 'Error al generar el reporte');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = () => {
        switch (reportType) {
            case 'mensual':
                generateReporteMensual();
                break;
            case 'estadoCuenta':
                generateEstadoCuenta();
                break;
            case 'gastos':
                generateAnalisisGastos();
                break;
            default:
                break;
        }
    };

    const downloadAsCSV = () => {
        if (!reportData) return;

        let csvContent = '';
        let filename = 'reporte';

        if (reportType === 'mensual') {
            filename = `reporte-mensual-${selectedMonth}-${selectedYear}.csv`;
            csvContent = 'REPORTE MENSUAL\n\n';
            csvContent += `Mes: ${selectedMonth}, Año: ${selectedYear}\n\n`;
            csvContent += `Total Ingresos,${reportData.totalIngresos}\n`;
            csvContent += `Total Egresos,${reportData.totalEgresos}\n`;
            csvContent += `Balance,${reportData.balance}\n\n`;

            csvContent += 'INGRESOS\n';
            csvContent += 'Fecha,Casa,Monto,Concepto,Referencia\n';
            reportData.ingresos?.forEach((ingreso) => {
                csvContent += `${ingreso.fecha},${ingreso.casaNumero},${ingreso.monto},"${ingreso.concepto}","${ingreso.referencia || ''}"\n`;
            });

            csvContent += '\nEGRESOS\n';
            csvContent += 'Fecha,Monto,Proveedor,Descripción,Categoría,Referencia\n';
            reportData.egresos?.forEach((egreso) => {
                csvContent += `${egreso.fecha},${egreso.monto},"${egreso.proveedor || ''}","${egreso.descripcion}","${egreso.categoria}","${egreso.referenciaFactura || ''}"\n`;
            });
        } else if (reportType === 'estadoCuenta') {
            filename = `estado-cuenta-${selectedMonth}-${selectedYear}.csv`;
            csvContent = 'ESTADO DE CUENTA GENERAL\n\n';
            csvContent += `Mes: ${selectedMonth}, Año: ${selectedYear}\n\n`;
            csvContent += `Total Casas,${reportData.totalCasas}\n`;
            csvContent += `Casas al Corriente,${reportData.casasAlCorriente}\n`;
            csvContent += `Casas Morosas,${reportData.casasMorosas}\n`;
            csvContent += `Total Adeudado,${reportData.totalAdeudado}\n`;
            csvContent += `Promedio Adeudo,${reportData.promedioAdeudo}\n\n`;
            csvContent += `Cargos del Mes,${reportData.totalCargosMes}\n`;
            csvContent += `Pagos del Mes,${reportData.totalPagosMes}\n`;
            csvContent += `Balance del Mes,${reportData.balanceMes}\n\n`;

            csvContent += 'DETALLE DE CASAS\n';
            csvContent += 'Casa,Usuario,Cargos Mes,Pagos Mes,Balance Mes,Adeudo Total,Estado\n';
            reportData.casas?.forEach((casa) => {
                const adeudo = Number(casa.adeudo || 0);
                const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
                const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
                const esMoroso = calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
                const estado = esMoroso ? 'Moroso' : 'Al Corriente';
                csvContent += `${casa.numeroCasa},${casa.usuarioNombre || 'N/A'},${casa.cargosMes || 0},${casa.pagosMes || 0},${casa.balanceMes || 0},${casa.adeudo},"${estado}"\n`;
            });
        } else if (reportType === 'gastos') {
            filename = `analisis-gastos-${selectedMonth}-${selectedYear}.csv`;
            csvContent = 'ANÁLISIS DE GASTOS\n\n';
            csvContent += `Mes: ${selectedMonth}, Año: ${selectedYear}\n`;
            csvContent += `Total Gastos,${reportData.totalGastos}\n\n`;

            csvContent += 'GASTOS POR CATEGORÍA\n';
            csvContent += 'Categoría,Monto,Porcentaje\n';
            reportData.gastosPorCategoria?.forEach((cat) => {
                const porcentaje = ((cat.monto / reportData.totalGastos) * 100).toFixed(2);
                csvContent += `"${cat.categoria}",${cat.monto},${porcentaje}%\n`;
            });

            csvContent += '\nDETALLE DE EGRESOS\n';
            csvContent += 'Fecha,Monto,Proveedor,Descripción,Categoría\n';
            reportData.egresos?.forEach((egreso) => {
                csvContent += `${egreso.fecha},${egreso.monto},"${egreso.proveedor || ''}","${egreso.descripcion}","${egreso.categoria}"\n`;
            });
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadMonthlyXlsx = async () => {
        if (!reportData) return;
        try {
            setLoading(true);
            const blob = await buildMonthlyWorkbook();
            if (!blob) return;
            const monthLabel = getMonthLabel(selectedMonth).replace(/\s+/g, '-');
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = `estado-cuenta-mensual-${monthLabel}-${selectedYear}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
        }).format(value || 0);
    };

    const getMonthLabel = (monthNumber) => {
        return new Date(selectedYear, monthNumber - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    };

    const formatFechaCorta = (fecha) => {
        if (!fecha) return '';
        try {
            // Si es un Timestamp de Firebase o string ISO
            const date = fecha instanceof Date ? fecha : new Date(fecha);
            if (isNaN(date.getTime())) return '';
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = String(date.getFullYear()).slice(-2);
            return `${d}-${m}-${y}`;
        } catch {
            return '';
        }
    };

    const buildMonthlyWorkbook = async () => {
        if (!reportData) return null;

        const monthLabel = getMonthLabel(selectedMonth);
        const monthTitle = `Estado de Cuenta Mensual ${monthLabel.charAt(0).toUpperCase()}${monthLabel.slice(1)}`;

        // Derive buckets
        const ingresos = reportData.ingresos || [];
        const egresos = reportData.egresos || [];
        const egresosFijos = egresos.filter((e) => {
            const tipo = (e.tipoGasto || e.tipo || '').toLowerCase();
            const cat = (e.categoria || '').toLowerCase();
            return tipo === 'fijo' || cat.includes('fijo');
        });
        const egresosVariables = egresos.filter((e) => !egresosFijos.includes(e));

        const totalIngresos = ingresos.reduce((acc, it) => acc + (Number(it.montoNeto ?? it.monto) || 0), 0);
        const totalEgresosFijos = egresosFijos.reduce((acc, it) => acc + (Number(it.monto) || 0), 0);
        const totalEgresosVariables = egresosVariables.reduce((acc, it) => acc + (Number(it.monto) || 0), 0);
        const totalEgresos = totalEgresosFijos + totalEgresosVariables;
        const saldoInicialFondo = Number(reportData.saldoInicialFondo || 0);
        const saldoFinalFondo = saldoInicialFondo + totalIngresos - totalEgresos;

        const wb = new ExcelJS.Workbook();
        wb.creator = 'Mi Fraccionamiento';
        wb.created = new Date();

        const currencyFmt = '$#,##0.00;[Red]$#,##0.00';
        const headerFont = { bold: true, color: { argb: 'FFFFFFFF' } };
        const centerAlign = { horizontal: 'center', vertical: 'middle' };

        // --- HOJA 1: INGRESOS ---
        const sheet1 = wb.addWorksheet('Ingresos', { views: [{ state: 'frozen', ySplit: 4 }] });
        sheet1.columns = [
            { key: 'fecha', width: 15 },
            { key: 'casa', width: 20 },
            { key: 'concepto', width: 35 },
            { key: 'referencia', width: 25 },
            { key: 'monto', width: 18 },
        ];

        // Título Hoja 1
        sheet1.mergeCells('A1:E1');
        const titleCell1 = sheet1.getCell('A1');
        titleCell1.value = monthTitle;
        titleCell1.font = { bold: true, size: 16, color: { argb: 'FF1E40AF' } };
        titleCell1.alignment = centerAlign;

        sheet1.mergeCells('A2:E2');
        const subtitleCell1 = sheet1.getCell('A2');
        subtitleCell1.value = 'DETALLE DE INGRESOS';
        subtitleCell1.font = { bold: true, size: 12 };
        subtitleCell1.alignment = centerAlign;

        // Tabla Ingresos
        sheet1.addTable({
            name: 'TableIngresos',
            ref: 'A4',
            headerRow: true,
            columns: [
                { name: 'Fecha' },
                { name: 'Casa / Fuente' },
                { name: 'Concepto' },
                { name: 'Referencia / Op' },
                { name: 'Monto' },
            ],
            rows: ingresos.flatMap((i) => {
                if (Array.isArray(i.pagos) && i.pagos.length > 0) {
                    return i.pagos.map(p => [
                        formatFechaCorta(i.fecha || p.fechaPago),
                        i.casaNumero || i.fuente || '—',
                        p.concepto || i.concepto || '',
                        p.referencia || i.referencia || '—',
                        Number(p.montoNeto ?? p.monto) || 0
                    ]);
                }
                return [[
                    formatFechaCorta(i.fecha),
                    i.casaNumero || i.fuente || '—',
                    i.concepto || i.descripcion || '',
                    i.referencia || '—',
                    Number(i.montoNeto ?? i.monto) || 0
                ]];
            }),
        });

        // Estilo encabezado Azul para Ingresos
        const headerIngresos = ['A4', 'B4', 'C4', 'D4', 'E4'];
        headerIngresos.forEach(ref => {
            const cell = sheet1.getCell(ref);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3B82F6' } };
            cell.font = headerFont;
            cell.alignment = centerAlign;
        });

        // Formato Moneda y Totales Ingresos
        sheet1.getColumn(5).numFmt = currencyFmt;
        const totalFilasIngresos = sheet1.rowCount - 3;
        const lastRowIng = 3 + totalFilasIngresos;
        sheet1.getCell(`D${lastRowIng + 1}`).value = 'TOTAL INGRESOS';
        sheet1.getCell(`D${lastRowIng + 1}`).font = { bold: true };
        sheet1.getCell(`E${lastRowIng + 1}`).value = totalIngresos;
        sheet1.getCell(`E${lastRowIng + 1}`).numFmt = currencyFmt;
        sheet1.getCell(`E${lastRowIng + 1}`).font = { bold: true, size: 12 };


        // --- HOJA 2: EGRESOS ---
        const sheet2 = wb.addWorksheet('Egresos', { views: [{ state: 'frozen', ySplit: 4 }] });
        // Definir anchos
        sheet2.getColumn(1).width = 12; // Fecha Fijos
        sheet2.getColumn(2).width = 30; // Desc Fijos
        sheet2.getColumn(3).width = 20; // Concepto Fijos
        sheet2.getColumn(4).width = 18; // Ref Fijos
        sheet2.getColumn(5).width = 15; // Monto Fijos
        sheet2.getColumn(6).width = 5;  // Espacio 1
        sheet2.getColumn(7).width = 5;  // Espacio 2
        sheet2.getColumn(8).width = 12; // Fecha Var
        sheet2.getColumn(9).width = 30; // Desc Var
        sheet2.getColumn(10).width = 20; // Concepto Var
        sheet2.getColumn(11).width = 18; // Ref Var
        sheet2.getColumn(12).width = 15; // Monto Var

        // Títulos Hoja 2
        sheet2.mergeCells('A1:L1');
        const titleCell2 = sheet2.getCell('A1');
        titleCell2.value = 'DETALLE DE EGRESOS DEL MES';
        titleCell2.font = { bold: true, size: 16 };
        titleCell2.alignment = centerAlign;

        sheet2.mergeCells('A3:E3');
        const subTitleFijos = sheet2.getCell('A3');
        subTitleFijos.value = 'EGRESOS FIJOS';
        subTitleFijos.font = { bold: true, color: { argb: 'FFC2410C' } };
        subTitleFijos.alignment = centerAlign;

        sheet2.mergeCells('H3:L3');
        const subTitleVar = sheet2.getCell('H3');
        subTitleVar.value = 'EGRESOS VARIABLES';
        subTitleVar.font = { bold: true, color: { argb: 'FF854D0E' } };
        subTitleVar.alignment = centerAlign;

        // Tabla Egresos Fijos (A4)
        sheet2.addTable({
            name: 'TableEgresosFijos',
            ref: 'A4',
            headerRow: true,
            columns: [
                { name: 'Fecha' },
                { name: 'Descripción' },
                { name: 'Concepto' },
                { name: 'Referencia / Op' },
                { name: 'Monto' },
            ],
            rows: egresosFijos.map((e) => [
                formatFechaCorta(e.fecha),
                (e.proveedor ? `${e.proveedor}\n${e.descripcion || ''}` : e.descripcion || ''),
                e.categoria || e.concepto || '',
                e.referenciaFactura || '—',
                Number(e.monto) || 0
            ]),
        });
        sheet2.getColumn(2).alignment = { wrapText: true, vertical: 'top' };

        // Estilo Naranja para Egresos Fijos
        const headerFijos = ['A4', 'B4', 'C4', 'D4', 'E4'];
        headerFijos.forEach(ref => {
            const cell = sheet2.getCell(ref);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF97316' } };
            cell.font = headerFont;
            cell.alignment = centerAlign;
        });

        // Tabla Egresos Variables (H4)
        sheet2.addTable({
            name: 'TableEgresosVariables',
            ref: 'H4',
            headerRow: true,
            columns: [
                { name: 'Fecha' },
                { name: 'Descripción' },
                { name: 'Concepto' },
                { name: 'Referencia / Op' },
                { name: 'Monto' },
            ],
            rows: egresosVariables.map((e) => [
                formatFechaCorta(e.fecha),
                (e.proveedor ? `${e.proveedor}\n${e.descripcion || ''}` : e.descripcion || ''),
                e.categoria || e.concepto || '',
                e.referenciaFactura || '—',
                Number(e.monto) || 0
            ]),
        });
        sheet2.getColumn(9).alignment = { wrapText: true, vertical: 'top' };

        // Estilo Beige para Egresos Variables
        const headerVar = ['H4', 'I4', 'J4', 'K4', 'L4'];
        headerVar.forEach(ref => {
            const cell = sheet2.getCell(ref);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6D3D1' } };
            cell.font = { bold: true, color: { argb: 'FF444444' } }; // Texto oscuro para fondo claro
            cell.alignment = centerAlign;
        });

        // Formatos Moneda Sheet 2
        sheet2.getColumn(5).numFmt = currencyFmt;
        sheet2.getColumn(12).numFmt = currencyFmt;

        // Totales Sheet 2
        const lastRowFijos = 4 + egresosFijos.length;
        sheet2.getCell(`D${lastRowFijos + 1}`).value = 'TOTAL FIJOS';
        sheet2.getCell(`D${lastRowFijos + 1}`).font = { bold: true };
        sheet2.getCell(`E${lastRowFijos + 1}`).value = totalEgresosFijos;
        sheet2.getCell(`E${lastRowFijos + 1}`).numFmt = currencyFmt;
        sheet2.getCell(`E${lastRowFijos + 1}`).font = { bold: true };

        const lastRowVar = 4 + egresosVariables.length;
        sheet2.getCell(`K${lastRowVar + 1}`).value = 'TOTAL VARIABLES';
        sheet2.getCell(`K${lastRowVar + 1}`).font = { bold: true };
        sheet2.getCell(`L${lastRowVar + 1}`).value = totalEgresosVariables;
        sheet2.getCell(`L${lastRowVar + 1}`).numFmt = currencyFmt;
        sheet2.getCell(`L${lastRowVar + 1}`).font = { bold: true };


        // --- HOJA 3: RESUMEN Y NOTAS ---
        const sheet3 = wb.addWorksheet('Resumen y Notas');
        sheet3.getColumn(1).width = 30;
        sheet3.getColumn(2).width = 25;
        sheet3.getColumn(4).width = 30;
        sheet3.getColumn(5).width = 25;

        sheet3.mergeCells('A1:B1');
        const titleRes = sheet3.getCell('A1');
        titleRes.value = 'RESUMEN FINANCIERO';
        titleRes.font = { bold: true, size: 14 };
        titleRes.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };

        const addResumenRow = (row, label, value, isBold = false, isCurrency = true) => {
            const cellL = sheet3.getCell(`A${row}`);
            const cellV = sheet3.getCell(`B${row}`);
            cellL.value = label;
            cellV.value = value;
            if (isBold) {
                cellL.font = { bold: true };
                cellV.font = { bold: true };
            }
            if (isCurrency) {
                cellV.numFmt = currencyFmt;
            }
            // Bordes
            cellL.border = { bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
            cellV.border = { bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
        };

        addResumenRow(3, 'Saldo inicial (Fondo)', saldoInicialFondo);
        addResumenRow(4, 'Ingresos del periodo (+)', totalIngresos);
        addResumenRow(5, 'Egresos del periodo (-)', totalEgresos);
        addResumenRow(7, 'SALDO FINAL DISPONIBLE', saldoFinalFondo, true);

        // Comparativa
        sheet3.mergeCells('A10:B10');
        const titleComp = sheet3.getCell('A10');
        titleComp.value = 'COMPARATIVA INGRESOS VS EGRESOS';
        titleComp.font = { bold: true, size: 12 };

        const result = totalIngresos - totalEgresos;
        addResumenRow(11, 'Total Ingresos', totalIngresos);
        addResumenRow(12, 'Total Egresos (Fijos + Var)', totalEgresos);
        const resCell = sheet3.getCell('B13');
        addResumenRow(13, 'RESULTADO DEL MES', result, true);
        resCell.font = { bold: true, color: { argb: result >= 0 ? 'FF0E9F6E' : 'FFD14343' } };

        // Notas y Comentarios (lado derecho)
        sheet3.mergeCells('D1:E1');
        const titleNotes = sheet3.getCell('D1');
        titleNotes.value = 'NOTAS Y COMENTARIOS';
        titleNotes.font = { bold: true, size: 12 };
        titleNotes.alignment = centerAlign;

        sheet3.mergeCells('D2:E13');
        const notesCell = sheet3.getCell('D2');
        notesCell.value = reportData.notes || reportData.notas || 'Sin comentarios adicionales.';
        notesCell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
        notesCell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Finish
        const buffer = await wb.xlsx.writeBuffer();
        return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    };

    const getTitle = () => {
        switch (reportType) {
            case 'mensual':
                const label = getMonthLabel(selectedMonth);
                return `Estado de Cuenta Mensual ${label.charAt(0).toUpperCase()}${label.slice(1)}`;
            case 'estadoCuenta':
                return 'Estado de Cuenta General';
            case 'gastos':
                return 'Análisis de Gastos';
            default:
                return 'Reporte';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="reportes-modal-overlay" onClick={onClose}>
            <div className={`reportes-modal-content ${!reportData ? 'reportes-modal-config' : ''}`} onClick={(e) => e.stopPropagation()}>
                {!reportData ? (
                    <>
                        {/* Cabecera Zen */}
                        <div className="reporte-config-header">
                            <div className="reporte-config-icon-badge esmeralda">
                                <FaFileInvoiceDollar />
                            </div>
                            <div className="reporte-config-header-text">
                                <h2>{getTitle()}</h2>
                                <p>Descarga y consulta el libro de ingresos y egresos contables</p>
                            </div>
                            <button className="reportes-close-btn" onClick={onClose} title="Cerrar">
                                <FaTimes />
                            </button>
                        </div>

                        <div className="reporte-modal-body reporte-config-body">
                            {/* Período Activo Badge */}
                            <div className="reporte-periodo-badge-row">
                                <span className="reporte-periodo-badge">
                                    <FaCalendarAlt style={{ marginRight: '6px' }} />
                                    Período Activo: <strong>{getMonthLabel(selectedMonth)}</strong>
                                </span>
                            </div>

                            {/* Atajos Rápidos */}
                            {(reportType === 'mensual' || reportType === 'gastos' || reportType === 'estadoCuenta') && (
                                <div className="reporte-shortcuts-section">
                                    <span className="reporte-shortcuts-label">Atajos Rápidos:</span>
                                    <div className="reporte-shortcuts-pills">
                                        <button
                                            type="button"
                                            className={`reporte-shortcut-pill ${esAtajoActivo('actual') ? 'active' : ''}`}
                                            onClick={() => aplicarAtajoPeriodo('actual')}
                                        >
                                            <FaBolt size={10} style={{ marginRight: '4px' }} /> Mes Actual
                                        </button>
                                        <button
                                            type="button"
                                            className={`reporte-shortcut-pill ${esAtajoActivo('anterior') ? 'active' : ''}`}
                                            onClick={() => aplicarAtajoPeriodo('anterior')}
                                        >
                                            <FaHistory size={10} style={{ marginRight: '4px' }} /> Mes Anterior
                                        </button>
                                        <button
                                            type="button"
                                            className={`reporte-shortcut-pill ${esAtajoActivo('haceDos') ? 'active' : ''}`}
                                            onClick={() => aplicarAtajoPeriodo('haceDos')}
                                        >
                                            <FaCalendarCheck size={10} style={{ marginRight: '4px' }} /> Hace 2 Meses
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Selectores de Mes y Año en Grid Dual */}
                            {(reportType === 'mensual' || reportType === 'gastos' || reportType === 'estadoCuenta') && (
                                <div className="reporte-selector-grid">
                                    <div className="reporte-select-card">
                                        <label>
                                            <FaCalendarAlt className="select-icon esmeralda" /> Mes del Reporte
                                        </label>
                                        <div className="select-wrapper">
                                            <select
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                                className="reporte-select-input"
                                            >
                                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                                                    const mesTxt = new Date(2024, m - 1).toLocaleDateString('es-MX', { month: 'long' });
                                                    return (
                                                        <option key={m} value={m}>
                                                            {mesTxt.charAt(0).toUpperCase() + mesTxt.slice(1)}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="reporte-select-card">
                                        <label>
                                            <FaCalendarCheck className="select-icon esmeralda" /> Año del Reporte
                                        </label>
                                        <div className="select-wrapper">
                                            <select
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                                className="reporte-select-input"
                                            >
                                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                                    <option key={y} value={y}>
                                                        {y}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tarjeta de Alcance y Contenido */}
                            <div className="reporte-scope-card">
                                <div className="reporte-scope-header">
                                    <FaFileExcel className="scope-icon esmeralda" />
                                    <span>Libro Contable de Ingresos y Egresos</span>
                                </div>
                                <div className="reporte-scope-items">
                                    <div className="scope-item">
                                        <span className="scope-dot income">•</span>
                                        <span><strong>Ingresos del Período:</strong> Fecha, número de lote/casa, concepto de cobro y referencia.</span>
                                    </div>
                                    <div className="scope-item">
                                        <span className="scope-dot expense">•</span>
                                        <span><strong>Egresos del Período:</strong> Proveedor, descripción del gasto, comprobante y categoría.</span>
                                    </div>
                                    <div className="scope-item">
                                        <span className="scope-dot balance">•</span>
                                        <span><strong>Formatos Disponibles:</strong> Vista previa interactiva, descarga en Excel (.xlsx) y CSV.</span>
                                    </div>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                        </div>

                        <div className="reporte-config-footer">
                            <button className="btn-zen-cancel" onClick={onClose}>Cancelar</button>
                            <button
                                className="btn-zen-generate esmeralda"
                                onClick={handleGenerateReport}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <FaSpinner className="spinner" style={{ marginRight: '6px' }} /> Generando Libro...
                                    </>
                                ) : (
                                    <>
                                        <FaDownload style={{ marginRight: '6px' }} /> Generar Libro de Ingresos/Egresos
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="reportes-modal-header">
                            <h2>{getTitle()}</h2>
                            <button className="reportes-close-btn" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="reportes-modal-body">
                            <div className="reportes-result">
                            {reportType === 'mensual' && (() => {
                                const ingresosArray = reportData.ingresos || [];
                                const egresosArray = reportData.egresos || [];

                                const totalPagesIngresos = Math.ceil(ingresosArray.length / ITEMS_PER_PAGE) || 1;
                                const totalPagesEgresos = Math.ceil(egresosArray.length / ITEMS_PER_PAGE) || 1;

                                const paginatedIngresos = ingresosArray.slice(
                                    (currentPageIngresos - 1) * ITEMS_PER_PAGE,
                                    currentPageIngresos * ITEMS_PER_PAGE
                                );
                                const paginatedEgresos = egresosArray.slice(
                                    (currentPageEgresos - 1) * ITEMS_PER_PAGE,
                                    currentPageEgresos * ITEMS_PER_PAGE
                                );

                                return (
                                    <div className="reporte-mensual premium-layout">
                                        <div className="premium-sticky-header">
                                            <div className="premium-summary-item">
                                                <label>Total Ingresos:</label>
                                                <span className="amount">{formatCurrency(reportData.totalIngresos)}</span>
                                            </div>
                                            <div className="premium-summary-item">
                                                <label>Total Egresos:</label>
                                                <span className="amount negative">{formatCurrency(reportData.totalEgresos)}</span>
                                            </div>
                                            <div className="premium-summary-item sum-total">
                                                <label>Balance:</label>
                                                <span className={`amount ${reportData.balance >= 0 ? 'positive' : 'negative'}`}>
                                                    {formatCurrency(reportData.balance)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="premium-tabs-container">
                                            <button
                                                className={`premium-tab ${activeTabIngEgr === 'ingresos' ? 'active' : ''}`}
                                                onClick={() => setActiveTabIngEgr('ingresos')}
                                            >
                                                Ingresos ({ingresosArray.length})
                                            </button>
                                            <button
                                                className={`premium-tab ${activeTabIngEgr === 'egresos' ? 'active' : ''}`}
                                                onClick={() => setActiveTabIngEgr('egresos')}
                                            >
                                                Egresos ({egresosArray.length})
                                            </button>
                                        </div>

                                        <div className="premium-details-container">
                                            {activeTabIngEgr === 'ingresos' && (
                                                <div className="premium-table-wrapper">
                                                    <table className="premium-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Fecha</th>
                                                                <th>Casa</th>
                                                                <th>Monto</th>
                                                                <th>Concepto</th>
                                                                <th>Referencia/Op</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {paginatedIngresos.length > 0 ? paginatedIngresos.map((ingreso, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{ingreso.fecha}</td>
                                                                    <td>{ingreso.casaNumero}</td>
                                                                    <td className="amount">{formatCurrency(ingreso.monto)}</td>
                                                                    <td>{ingreso.concepto}</td>
                                                                    <td>{ingreso.referencia || '—'}</td>
                                                                </tr>
                                                            )) : (
                                                                <tr>
                                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No hay ingresos en este mes.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>

                                                    {totalPagesIngresos > 1 && (
                                                        <div className="premium-pagination">
                                                            <button disabled={currentPageIngresos === 1} onClick={handlePrevPageIngresos}>&lsaquo; Anterior</button>
                                                            <span>Página {currentPageIngresos} de {totalPagesIngresos}</span>
                                                            <button disabled={currentPageIngresos === totalPagesIngresos} onClick={handleNextPageIngresos}>Siguiente &rsaquo;</button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {activeTabIngEgr === 'egresos' && (
                                                <div className="premium-table-wrapper">
                                                    <table className="premium-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Fecha</th>
                                                                <th>Descripción</th>
                                                                <th>Categoría</th>
                                                                <th>Referencia/Op</th>
                                                                <th>Monto</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {paginatedEgresos.length > 0 ? paginatedEgresos.map((egreso, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{egreso.fecha}</td>
                                                                    <td>
                                                                        {egreso.proveedor ? (
                                                                            <>
                                                                                <div><strong>{egreso.proveedor}</strong></div>
                                                                                <div style={{ fontSize: '0.85em', color: '#555' }}>{egreso.descripcion || 'Sin descripción'}</div>
                                                                            </>
                                                                        ) : (
                                                                            egreso.descripcion || 'Sin descripción'
                                                                        )}
                                                                    </td>
                                                                    <td>{egreso.categoria}</td>
                                                                    <td>{egreso.referenciaFactura || '—'}</td>
                                                                    <td className="amount negative">{formatCurrency(egreso.monto)}</td>
                                                                </tr>
                                                            )) : (
                                                                <tr>
                                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No hay egresos en este mes.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>

                                                    {totalPagesEgresos > 1 && (
                                                        <div className="premium-pagination">
                                                            <button disabled={currentPageEgresos === 1} onClick={handlePrevPageEgresos}>&lsaquo; Anterior</button>
                                                            <span>Página {currentPageEgresos} de {totalPagesEgresos}</span>
                                                            <button disabled={currentPageEgresos === totalPagesEgresos} onClick={handleNextPageEgresos}>Siguiente &rsaquo;</button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {reportType === 'estadoCuenta' && (
                                <div className="estado-cuenta-report">
                                    <div className="estado-summary">
                                        <div className="summary-box">
                                            <label>Total Casas</label>
                                            <span className="value">{reportData.totalCasas}</span>
                                        </div>
                                        <div className="summary-box success">
                                            <label>Al Corriente</label>
                                            <span className="value">{reportData.casasAlCorriente}</span>
                                        </div>
                                        <div className="summary-box danger">
                                            <label>Morosas</label>
                                            <span className="value">{reportData.casasMorosas}</span>
                                        </div>
                                        <div className="summary-box">
                                            <label>Total Adeudado</label>
                                            <span className="value">{formatCurrency(reportData.totalAdeudado)}</span>
                                        </div>
                                        <div className="summary-box">
                                            <label>Promedio Adeudo</label>
                                            <span className="value">{formatCurrency(reportData.promedioAdeudo)}</span>
                                        </div>
                                    </div>

                                    <div className="estado-summary">
                                        <div className="summary-box">
                                            <label>Cargos del Mes</label>
                                            <span className="value negative">{formatCurrency(reportData.totalCargosMes)}</span>
                                        </div>
                                        <div className="summary-box">
                                            <label>Pagos del Mes</label>
                                            <span className="value positive">{formatCurrency(reportData.totalPagosMes)}</span>
                                        </div>
                                        <div className="summary-box">
                                            <label>Balance del Mes</label>
                                            <span className={`value ${reportData.balanceMes >= 0 ? 'positive' : 'negative'}`}>
                                                {formatCurrency(reportData.balanceMes)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="casas-list">
                                        <h4>Detalle de Casas</h4>
                                        <table className="details-table">
                                            <thead>
                                                <tr>
                                                    <th>Casa</th>
                                                    <th>Usuario</th>
                                                    <th>Cargos Mes</th>
                                                    <th>Pagos Mes</th>
                                                    <th>Balance Mes</th>
                                                    <th>Adeudo Total</th>
                                                    <th>Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.casas?.map((casa, idx) => {
                                                    const adeudo = Number(casa.adeudo || 0);
                                                    const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
                                                    const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
                                                    const esMoroso = calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);

                                                    return (
                                                        <tr key={idx} className={esMoroso ? 'morosa' : ''}>
                                                            <td>{casa.numeroCasa}</td>
                                                            <td>{casa.usuarioNombre || 'Sin asignar'}</td>
                                                            <td className="amount negative">{formatCurrency(casa.cargosMes || 0)}</td>
                                                            <td className="amount positive">{formatCurrency(casa.pagosMes || 0)}</td>
                                                            <td className={`amount ${(casa.balanceMes || 0) >= 0 ? 'positive' : 'negative'}`}>
                                                                {formatCurrency(casa.balanceMes || 0)}
                                                            </td>
                                                            <td className="amount">{formatCurrency(casa.adeudo)}</td>
                                                            <td>
                                                                <span className={`status-badge ${esMoroso ? 'morosa' : 'corriente'}`}>
                                                                    {esMoroso ? 'Moroso' : 'Al Corriente'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {reportType === 'gastos' && (
                                <div className="gastos-report">
                                    <div className="gastos-summary">
                                        <div className="summary-item">
                                            <label>Total Gastos:</label>
                                            <span className="amount">{formatCurrency(reportData.totalGastos)}</span>
                                        </div>
                                    </div>

                                    <div className="gastos-by-category">
                                        <h4>Gastos por Categoría</h4>
                                        <table className="details-table">
                                            <thead>
                                                <tr>
                                                    <th>Categoría</th>
                                                    <th>Monto</th>
                                                    <th>Porcentaje</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.gastosPorCategoria?.map((cat, idx) => {
                                                    const porcentaje = ((cat.monto / reportData.totalGastos) * 100).toFixed(2);
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{cat.categoria}</td>
                                                            <td className="amount">{formatCurrency(cat.monto)}</td>
                                                            <td>{porcentaje}%</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="details-section">
                                        <h4>Detalle de Egresos</h4>
                                        <table className="details-table">
                                            <thead>
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Descripción</th>
                                                    <th>Categoría</th>
                                                    <th>Monto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.egresos?.map((egreso, idx) => (
                                                    <tr key={idx}>
                                                        <td>{egreso.fecha}</td>
                                                        <td>
                                                            {egreso.proveedor ? (
                                                                <>
                                                                    <div><strong>{egreso.proveedor}</strong></div>
                                                                    <div style={{ fontSize: '0.85em', color: '#555' }}>{egreso.descripcion || 'Sin descripción'}</div>
                                                                </>
                                                            ) : (
                                                                egreso.descripcion || 'Sin descripción'
                                                            )}
                                                        </td>
                                                        <td>{egreso.categoria}</td>
                                                        <td className="amount">{formatCurrency(egreso.monto)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className="reportes-actions">
                                {reportType === 'mensual' && (
                                    <button className="btn-download" onClick={downloadMonthlyXlsx} disabled={loading}>
                                        <FaDownload /> Descargar XLSX (completo)
                                    </button>
                                )}
                                <button className="btn-new-report" onClick={() => setReportData(null)}>
                                    Nuevo Reporte
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    </div>
);
};

export default ReportesModal;
